import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import BookingItemDetails from "@/components/site/BookingItemDetails";
import TicketPrintView from "@/components/site/TicketPrintView";
import { useAuth } from "@/lib/auth";
import { useCurrency } from "@/lib/currency";
import { toast } from "sonner";
import {
  ArrowLeft, Plus, Printer, Download, Plane, ShieldCheck, Ban,
  CheckCircle2, Clock, XCircle, Ticket,
} from "lucide-react";

function statusBadge(s) {
  const map = {
    confirmed: "bg-emerald-100 text-emerald-700",
    pending_payment: "bg-amber-100 text-amber-700",
    cancelled: "bg-red-100 text-red-700",
  };
  return <Badge className={`${map[s] || "bg-slate-100 text-slate-700"} border-0 capitalize`}>{s.replace("_", " ")}</Badge>;
}

function cancelReqBadge(s) {
  if (!s) return null;
  const map = {
    pending: { c: "bg-amber-100 text-amber-700", Icon: Clock },
    approved: { c: "bg-emerald-100 text-emerald-700", Icon: CheckCircle2 },
    rejected: { c: "bg-red-100 text-red-700", Icon: XCircle },
  }[s] || { c: "bg-slate-100 text-slate-700", Icon: Clock };
  const Icon = map.Icon;
  return <Badge className={`${map.c} border-0 capitalize`}><Icon className="w-3 h-3 mr-1" /> Cancel {s}</Badge>;
}

export default function BookingDetails() {
  const { bookingId } = useParams();
  const { user, loading } = useAuth();
  const { format: fmt, currency: dispCcy } = useCurrency();
  const navigate = useNavigate();
  const [booking, setBooking] = useState(null);
  const [addons, setAddons] = useState([]);
  const [openCancel, setOpenCancel] = useState(false);
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [pdfBusy, setPdfBusy] = useState(false);
  const printRef = useRef(null);

  const downloadPdf = async () => {
    if (!printRef.current) return;
    setPdfBusy(true);
    try {
      const html2pdf = (await import("html2pdf.js")).default;
      const filename = `E-Ticket-${booking?.travelport_locator || booking?.amadeus_locator || booking?.pnr || bookingId}.pdf`;
      await html2pdf().set({
        margin:       [10, 10, 10, 10],
        filename,
        image:        { type: "jpeg", quality: 0.98 },
        html2canvas:  { scale: 2, useCORS: true, backgroundColor: "#ffffff", logging: false },
        jsPDF:        { unit: "mm", format: "a4", orientation: "portrait" },
        pagebreak:    { mode: ["css", "legacy"] },
      }).from(printRef.current).save();
      toast.success("Your e-ticket PDF is downloading.");
    } catch (e) {
      toast.error("Could not generate PDF. Please try again.");
      console.error("PDF download failed:", e);
    } finally { setPdfBusy(false); }
  };

  const load = async () => {
    try {
      const { data } = await api.get(`/bookings/${bookingId}`);
      setBooking(data);
    } catch (_e) { toast.error("Booking not found"); navigate("/my-bookings"); return; }
    try {
      const { data } = await api.get(`/bookings/${bookingId}/addons`);
      setAddons(data);
    } catch (_e) {}
  };

  useEffect(() => {
    if (loading) return;
    if (!user) { navigate("/"); return; }
    load();
    // eslint-disable-next-line
  }, [bookingId, user, loading]);

  if (!booking) return <div className="max-w-5xl mx-auto px-6 py-16 text-slate-500">Loading...</div>;

  const isFlight = booking.booking_type === "flight";
  const isConfirmed = booking.status === "confirmed";
  const cancelPending = booking.cancel_request_status === "pending";
  const cancelable = booking.status !== "cancelled" && !cancelPending;

  const submitCancel = async () => {
    setBusy(true);
    try {
      await api.post(`/bookings/${bookingId}/cancel-request`, { reason });
      toast.success("Cancellation request submitted. Our team will review it shortly.");
      setOpenCancel(false);
      setReason("");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not submit request");
    } finally { setBusy(false); }
  };

  const paidAddons = addons.filter((a) => a.payment_status === "paid");

  return (
    <div className="max-w-5xl mx-auto px-6 py-8">
      {/* Print stylesheet — on window.print():
          • Hide the Navbar (<header>), Footer, and every .no-print / screen-only control.
          • Hide the on-screen booking card (the gradient e-ticket) — the clean
            TicketPrintView further down replaces it.
          • Reveal the .print-only container (which wraps TicketPrintView).
          • Force -webkit-print-color-adjust so logos + brand colours render. */}
      <style>{`
        .print-only { display: none; }
        @media print {
          header, footer, nav, [data-print-hide], .no-print, .screen-only { display: none !important; }
          html, body { background: #fff !important; margin: 0 !important; padding: 0 !important; }
          .print-only { display: block !important; }
          .print-page { max-width: 100% !important; padding: 0 !important; margin: 0 !important; }
          * { -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; }
          @page { margin: 12mm; size: A4; }
        }
      `}</style>

      {/* Print-only clean e-ticket. Rendered off-screen normally (for html2pdf
          to snapshot on click) and swapped in as the ONLY visible content when
          the user hits Print. */}
      <div className="print-only">
        <TicketPrintView booking={booking} addons={addons} />
      </div>
      {/* Off-screen copy (positioned via absolute) used exclusively by
          html2pdf.js on Download PDF click. */}
      <div aria-hidden="true" className="screen-only" style={{ position: "absolute", left: "-10000px", top: 0, width: 794 }}>
        <TicketPrintView ref={printRef} booking={booking} addons={addons} />
      </div>

      <div className="flex items-center justify-between flex-wrap gap-3 screen-only">
        <Button variant="ghost" onClick={() => navigate("/my-bookings")} className="rounded-full">
          <ArrowLeft className="w-4 h-4 mr-1" /> Back to my bookings
        </Button>
        <div className="flex items-center gap-2">
          {isConfirmed && (
            <>
              <Button data-testid="download-pdf-btn" variant="outline" className="rounded-full"
                      disabled={pdfBusy} onClick={downloadPdf}>
                <Download className="w-4 h-4 mr-2" /> {pdfBusy ? "Generating..." : "Download PDF"}
              </Button>
              <Button data-testid="print-ticket-btn" variant="outline" className="rounded-full"
                      onClick={() => window.print()}>
                <Printer className="w-4 h-4 mr-2" /> Print
              </Button>
            </>
          )}
          {isFlight && isConfirmed && (
            <Button data-testid="addons-cta-btn" onClick={() => navigate(`/bookings/${bookingId}/addons`)}
                    className="rounded-full bg-[var(--brand-primary)] hover:bg-[var(--brand-primary-dark)]">
              <Plus className="w-4 h-4 mr-2" /> Add seats / bags / meals
            </Button>
          )}
          {cancelable && (
            <Button data-testid="request-cancel-btn" variant="outline"
                    className="rounded-full text-red-600 hover:bg-red-50 border-red-200"
                    onClick={() => setOpenCancel(true)}>
              <Ban className="w-4 h-4 mr-2" /> Request cancellation
            </Button>
          )}
        </div>
      </div>

      {/* E-Ticket header (screen-only — on Print the clean TicketPrintView
          replaces this whole card via CSS @media rules above). */}
      <Card className="mt-5 overflow-hidden screen-only">
        <div className="bg-gradient-to-br from-[var(--brand-accent)] to-[var(--brand-primary-dark)] p-6 text-white flex items-center justify-between flex-wrap gap-4">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-xl bg-white/15 flex items-center justify-center">
              {isFlight ? <Plane className="w-6 h-6 text-white" /> : <Ticket className="w-6 h-6 text-white" />}
            </div>
            <div>
              <div className="text-[11px] uppercase tracking-[0.22em] text-[var(--brand-secondary)]">
                {isConfirmed ? "E-Ticket / Confirmation" : "Booking"}
              </div>
              <h1 className="font-heading text-2xl font-semibold">{booking.booking_type.charAt(0).toUpperCase() + booking.booking_type.slice(1)} booking</h1>
              {(() => {
                const supplierPnr = booking.travelport_locator || booking.amadeus_locator || "";
                const ticketNo = (booking.travelport_tickets?.[0]?.number)
                  || (booking.amadeus_tickets?.[0]?.number)
                  || "";
                return (
                  <div className="text-xs text-teal-50/90 mt-0.5 flex flex-wrap items-center gap-x-3 gap-y-0.5">
                    {supplierPnr ? (
                      <span>Airline PNR <span className="font-mono font-bold text-white">{supplierPnr}</span></span>
                    ) : (
                      <span>PNR <span className="font-mono font-bold text-white">{booking.pnr}</span></span>
                    )}
                    {ticketNo && (
                      <span>· Ticket <span className="font-mono font-bold text-white">{ticketNo}</span></span>
                    )}
                    {supplierPnr && (
                      <span className="text-[10px] text-teal-100/80">· Ref {booking.pnr}</span>
                    )}
                    <span>· {new Date(booking.created_at).toLocaleString()}</span>
                  </div>
                );
              })()}
            </div>
          </div>
          <div className="flex items-center gap-2">
            {statusBadge(booking.status)}
            {(() => {
              const noTicket = !(booking.travelport_tickets?.length || booking.amadeus_tickets?.length);
              const isPaid = (booking.payment_status || "").toLowerCase() === "paid" || booking.status === "confirmed";
              if (booking.booking_type === "flight" && isPaid && noTicket) {
                return (
                  <span data-testid="ticket-pending-badge" className="text-[10px] px-2 py-1 rounded-full bg-amber-100 text-amber-900 font-bold uppercase tracking-wider">
                    E-ticket pending
                  </span>
                );
              }
              return null;
            })()}
            {cancelReqBadge(booking.cancel_request_status)}
          </div>
        </div>

        {(() => {
          const noTicket = !(booking.travelport_tickets?.length || booking.amadeus_tickets?.length);
          const isPaid = (booking.payment_status || "").toLowerCase() === "paid" || booking.status === "confirmed";
          if (booking.booking_type === "flight" && isPaid && noTicket) {
            return (
              <div data-testid="b2c-ticket-pending-banner" className="px-6 py-3 bg-amber-50 border-b border-amber-200 text-sm text-amber-900">
                <i className="fa-solid fa-circle-info mr-2"></i>
                Your payment is confirmed. We&apos;re finalising your e-ticket with the airline — you&apos;ll receive an email with the airline PNR and ticket number shortly. Most issues clear within a few minutes; if not, our team will reach out.
              </div>
            );
          }
          return null;
        })()}

        <div className="p-6">
          <BookingItemDetails bookingType={booking.booking_type} item={booking.item} travelers={booking.travelers} />

          <div className="mt-6 pt-5 border-t border-slate-100 grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
            <div>
              <div className="text-xs uppercase tracking-wider text-slate-400 font-bold">Contact</div>
              <div className="text-[var(--brand-accent)] mt-1">{booking.contact?.name}</div>
              <div className="text-slate-500 text-xs">{booking.contact?.email}{booking.contact?.phone ? ` · ${booking.contact.phone}` : ""}</div>
            </div>
            <div>
              <div className="text-xs uppercase tracking-wider text-slate-400 font-bold">Payment</div>
              <div className="text-[var(--brand-accent)] mt-1 capitalize">{booking.payment_status || "—"}</div>
              {booking.paid_at && <div className="text-slate-500 text-xs">Paid: {new Date(booking.paid_at).toLocaleString()}</div>}
            </div>
            <div className="md:text-right">
              <div className="text-xs uppercase tracking-wider text-slate-400 font-bold">Total</div>
              <div className="font-heading text-2xl font-bold text-[var(--brand-accent)]">{fmt(Number(booking.total_amount))}</div>
            </div>
          </div>
        </div>
      </Card>

      {/* Purchased Add-ons */}
      {paidAddons.length > 0 && (
        <Card className="mt-5 p-6 screen-only" data-testid="addons-purchased">
          <h2 className="font-heading text-lg font-semibold text-[var(--brand-accent)] flex items-center gap-2">
            <ShieldCheck className="w-4 h-4 text-[var(--brand-primary)]" /> Purchased add-ons
          </h2>
          <div className="mt-3 space-y-2">
            {paidAddons.map((a) => (
              <div key={a.id} className="border border-slate-100 rounded-xl p-3">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-xs text-slate-500">{new Date(a.created_at).toLocaleString()}</span>
                  <span className="font-heading font-bold text-[var(--brand-accent)]">{fmt(Number(a.amount))}</span>
                </div>
                <ul className="mt-2 grid grid-cols-1 md:grid-cols-2 gap-1.5 text-xs text-slate-600">
                  {(a.items || []).map((it, i) => (
                    <li key={i} className="flex items-center gap-1.5">
                      <span className="capitalize bg-teal-50 text-teal-700 px-1.5 py-0.5 rounded text-[10px]">{it.kind}</span>
                      Segment {Number(it.segment_index) + 1} · Pax {Number(it.pax_index) + 1} · {it.label} <span className="text-slate-400">(+{fmt(Number(it.price))})</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </Card>
      )}

      {/* Cancel Dialog */}
      <Dialog open={openCancel} onOpenChange={setOpenCancel}>
        <DialogContent data-testid="cancel-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl text-[var(--brand-accent)]">Request cancellation</DialogTitle>
            <DialogDescription>
              Cancellations are not automatic. Our team reviews each request and processes the refund as per the fare rules of the supplier. You'll see the status here under <span className="font-medium text-[var(--brand-accent)]">Cancel pending / approved / rejected</span>.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <Label>Reason (optional)</Label>
            <Textarea data-testid="cancel-reason-input" rows={4} placeholder="Tell us why you'd like to cancel..."
                      value={reason} onChange={(e) => setReason(e.target.value)} />
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setOpenCancel(false)}>Keep booking</Button>
            <Button data-testid="cancel-submit-btn" disabled={busy} onClick={submitCancel}
                    className="bg-red-600 hover:bg-red-700">
              {busy ? "Submitting..." : "Submit request"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
